import React, { useState } from 'react';
import { useResponsive } from '../../hooks/useResponsive';
import { ChevronDown } from 'lucide-react';

const safeToLocaleString = (value) => {
  if (value === null || value === undefined || isNaN(value)) return "0";
  return value.toLocaleString("en-US", { maximumFractionDigits: 2 });
};

const formatValue = (item, col) => {
  const value = item[col.field] ?? item[col.key] ?? 0;
  switch (col.format) {
    case 'number':
      return safeToLocaleString(value);
    case 'currency':
      return `${col?.currency || ""} ${safeToLocaleString(value.toFixed(2))}`;
    case 'percentage':
      return `${(value || 0).toFixed(2)}%`;
    default:
      return value ?? '-';
  }
};

const DataTable = ({
  columns = [], // [{ key, label, field, format, render }]
  items = [],
  loading = false,
  emptyMessage = "No hay resultados.",
  onRowClick = null, // Función que se ejecuta al hacer click en una fila
  mobileCardRenderer = null, // Custom mobile card renderer
  expandable = false, // Allow expanding rows
}) => {
  const { isMobile } = useResponsive();
  const [expandedId, setExpandedId] = useState(null);

  // Mobile rendering - card view
  if (isMobile) {
    return (
      <div data-testid="card-view" className="space-y-3 px-4 py-4">
        {loading ? (
          Array.from({ length: 3 }).map((_, idx) => (
            <div
              key={`loading-${idx}`}
              className="bg-white/10 rounded-lg p-4 space-y-2 animate-pulse"
            >
              <div className="h-4 bg-white/20 rounded w-3/4" />
              <div className="h-3 bg-white/20 rounded w-full" />
              <div className="h-3 bg-white/20 rounded w-1/2" />
            </div>
          ))
        ) : items.length > 0 ? (
          items.map((item, idx) => {
            const isExpanded = expandedId === item.id;
            const cardElement = mobileCardRenderer ? mobileCardRenderer(item) : null;

            return (
              <div
                key={item.id ?? `card-${idx}`}
                data-testid="mobile-card"
                className="bg-white/10 backdrop-blur-md rounded-lg border border-white/20 overflow-hidden hover:bg-white/15 transition-all duration-200"
              >
                <div
                  onClick={() => {
                    onRowClick?.(item);
                    if (expandable) {
                      setExpandedId(isExpanded ? null : item.id ?? idx);
                    }
                  }}
                  className="min-h-[44px] p-4 cursor-pointer flex items-center justify-between group"
                >
                  <div className="flex-1">
                    {cardElement || <div className="text-white/80 text-sm">Fila de datos</div>}
                  </div>
                  {expandable && (
                    <ChevronDown
                      size={20}
                      className={`text-white/60 ml-2 transition-transform duration-200 ${
                        isExpanded ? 'rotate-180' : ''
                      }`}
                    />
                  )}
                </div>

                {expandable && isExpanded && (
                  <div className="border-t border-white/10 bg-white/5 px-4 py-3">
                    <div className="text-xs text-white/60 space-y-2">
                      {columns.map((col) => (
                        <div key={col.key} className="flex justify-between">
                          <span className="font-medium text-white/70">{col.label}:</span>
                          <span className="text-white/80">
                            {item[col.field] ?? item[col.key] ?? '-'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div className="flex flex-col items-center justify-center py-12 space-y-3">
            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-white/50"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m13-8l-4 4m0 0l-4-4m4 4V3"
                />
              </svg>
            </div>
            <p className="text-white/70 font-medium text-sm text-center">{emptyMessage}</p>
          </div>
        )}
      </div>
    );
  }

  // Desktop rendering - traditional table
  return (
    <div data-testid="table-view" className="overflow-x-auto table-scroll-hide">
      <table className="min-w-full">
        <thead>
          <tr className="bg-white/5 backdrop-blur-md border-b border-white/20">
            {columns.map((col, idx) => (
              <th
                key={`head-${col.key ?? idx}`}
                className="px-4 sm:px-6 py-3 text-center text-xs font-semibold text-white/80 uppercase tracking-wide"
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white/10 backdrop-blur-md">
          {loading ? (
            Array.from({ length: 5 }).map((_, rowIdx) => (
              <tr key={`loading-${rowIdx}`} className="border-b border-white/10">
                {columns.map((col, colIdx) => (
                  <td key={`loading-${rowIdx}-${col.key ?? colIdx}`} className="px-6 py-6">
                    <div className="flex items-center justify-center">
                      <div className="h-4 w-20 bg-white/20 rounded-lg animate-pulse" />
                    </div>
                  </td>
                ))}
              </tr>
            ))
          ) : items.length > 0 ? (
            items.map((item, rowIdx) => (
              <tr 
                key={item.id ?? `row-${rowIdx}`} 
                onClick={() => onRowClick && onRowClick(item)}
                className="border-b border-white/10 hover:bg-white/20 hover:shadow-lg hover:scale-[1.01] transition-all duration-300 group cursor-pointer relative hover:z-10 hover:border-l-4 hover:border-l-fuchsia-500"
              >
                {columns.map((col, colIdx) => {
                  const display = col.render
                    ? col.render(item[col.field] ?? item[col.key], item)
                    : formatValue(item, col);

                  return (
                    <td
                      key={`cell-${rowIdx}-${col.key ?? colIdx}`}
                      className="px-6 py-6 whitespace-nowrap text-sm text-white/90 text-center font-medium group-hover:text-white transition-all duration-300"
                    >
                      <div className="flex items-center justify-center">
                        {display}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={columns.length}
                className="text-center py-12"
              >
                <div className="flex flex-col items-center space-y-3">
                  <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m13-8l-4 4m0 0l-4-4m4 4V3" />
                    </svg>
                  </div>
                  <p className="text-white/70 font-medium">{emptyMessage}</p>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default DataTable;
